import React, { JSX } from 'react';
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material';
import {
  AccountCircle,
  Cancel,
  Edit,
  Email,
  LocationOn,
  Phone,
  PhotoCamera,
  Save,
} from '@mui/icons-material';
import * as Types from 'types/types';
import { ProfileField, ProfileStaticField } from './ProfileFields';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

function publicLabel(isPublic: boolean | undefined, username: string): string {
  return isPublic
    ? 'Your account will appear in candidate lists and searches on Backstory.'
    : `Your account will not be listed as a candidate. You can still share your profile by users navigating to '/u/${username}'.`;
}

interface BasicInfoButtonsProps {
  editMode: boolean;
  isMobile: boolean;
  loading: boolean;
  onCancel: () => void;
  onSave: () => void;
  onEdit: () => void;
}

function BasicInfoButtons({
  editMode,
  isMobile,
  loading,
  onCancel,
  onSave,
  onEdit,
}: BasicInfoButtonsProps): JSX.Element {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'flex-end',
        gap: 2,
        mt: { xs: 2, sm: 0 },
      }}
    >
      {editMode ? (
        <>
          <Button variant="outlined" onClick={onCancel} startIcon={<Cancel />} fullWidth={isMobile}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Save />}
            disabled={loading}
            fullWidth={isMobile}
          >
            Save
          </Button>
        </>
      ) : (
        <Button variant="outlined" onClick={onEdit} startIcon={<Edit />} fullWidth={isMobile}>
          Edit Info
        </Button>
      )}
    </Box>
  );
}

interface BasicInfoTabProps {
  candidate: Types.Candidate;
  formData: Partial<Types.Candidate>;
  editMode: { [key: string]: boolean };
  isMobile: boolean;
  loading: boolean;
  profileImage: string;
  primaryColor: string;
  handleInputChange: (field: string, value: boolean | string | Types.Location) => void;
  setFormData: (data: Partial<Types.Candidate>) => void;
  handleImageUpload: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  handleCancel: (section: string) => void;
  handleSave: () => Promise<void>;
  toggleEditMode: (section: string) => void;
}

function BasicInfoTab(props: BasicInfoTabProps): JSX.Element {
  const { candidate, formData, editMode, isMobile, loading, profileImage, primaryColor } = props;
  const inEdit = !!editMode.basic;
  const locationText = candidate.location?.city
    ? `${candidate.location?.city} ${candidate.location?.country}`
    : candidate.location?.text || 'Not provided';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        '& .entry': { flexDirection: 'column', fontSize: '0.9rem', display: 'flex', mt: 1 },
        '& .title': { display: 'flex', fontWeight: 'bold' },
      }}
    >
      <Box sx={{ textAlign: 'center', mb: { xs: 1, sm: 2 } }}>
        <Avatar
          src={profileImage}
          sx={{
            width: { xs: 80, sm: 120 },
            height: { xs: 80, sm: 120 },
            mb: { xs: 1, sm: 2 },
            mx: 'auto',
            border: `2px solid ${primaryColor}`,
          }}
        >
          {!profileImage && <AccountCircle sx={{ fontSize: { xs: 50, sm: 80 } }} />}
        </Avatar>
        {inEdit && (
          <>
            <IconButton
              color="primary"
              aria-label="upload picture"
              component="label"
              size={isMobile ? 'small' : 'medium'}
            >
              <PhotoCamera />
              <VisuallyHiddenInput
                type="file"
                accept="image/*"
                onChange={props.handleImageUpload}
              />
            </IconButton>
            <Typography
              variant="caption"
              color="textSecondary"
              sx={{ textAlign: 'center', fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
            >
              Update profile photo
            </Typography>
          </>
        )}
      </Box>
      <Box className="entry" sx={{ alignItems: 'center' }}>
        {inEdit ? (
          <FormControlLabel
            control={
              <Switch
                checked={formData.isPublic}
                onChange={(e): void => props.handleInputChange('isPublic', e.target.checked)}
              />
            }
            label={publicLabel(formData.isPublic, candidate.username)}
          />
        ) : (
          <>{publicLabel(formData.isPublic, candidate.username)}</>
        )}
      </Box>
      <ProfileField
        label="First Name"
        value={candidate.firstName}
        editing={inEdit}
        editValue={formData.firstName || ''}
        onChange={(value): void => props.handleInputChange('firstName', value)}
      />
      <ProfileField
        label="Last Name"
        value={candidate.lastName}
        editing={inEdit}
        editValue={formData.lastName || ''}
        onChange={(value): void => props.handleInputChange('lastName', value)}
      />
      <ProfileStaticField
        icon={<Email sx={{ mr: 1, verticalAlign: 'middle' }} />}
        label="Email"
        value={candidate.email}
      />
      <ProfileField
        label="Phone"
        value={candidate.phone || 'Not provided'}
        editing={inEdit}
        editValue={formData.phone || ''}
        onChange={(value): void => props.handleInputChange('phone', value)}
        icon={<Phone sx={{ mr: 1, verticalAlign: 'middle' }} />}
      />
      <ProfileField
        label="Professional Summary"
        value={candidate.description || 'No summary provided'}
        editing={inEdit}
        editValue={formData.description || ''}
        onChange={(value): void => props.handleInputChange('description', value)}
        multiline
      />
      <Box className="entry">
        {inEdit ? (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Location"
            value={formData.location?.text || ''}
            variant="outlined"
            onChange={(e): void =>
              props.setFormData({
                ...formData,
                location: { ...formData.location, text: e.target.value },
              })
            }
          />
        ) : (
          <ProfileStaticField
            icon={<LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />}
            label="Location"
            value={locationText}
          />
        )}
      </Box>
      <Box className="entry">
        <BasicInfoButtons
          editMode={inEdit}
          isMobile={isMobile}
          loading={loading}
          onCancel={(): void => props.handleCancel('basic')}
          onSave={props.handleSave}
          onEdit={(): void => props.toggleEditMode('basic')}
        />
      </Box>
    </Box>
  );
}

export { BasicInfoTab };
